import axios from "axios";
import type {RouteLocationNormalized} from "vue-router";

/**
 * The head data of a page (the same that the server writes into the first HTML answer).
 */
export interface DocumentMetaInterface {
	title: string,
	description: string,
	keywords: string,
	canonical: string | null,
	seo_manager: { attribute: string, name: string, content: string }[],
	json_ld: object[],
}

const SHARING_TAGS = 'meta[property^="og:"], meta[property^="fb:"], meta[property^="article:"], meta[name^="twitter:"]';

const setNamedMeta = (name: string, content: string): void => {
	let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
	if (!content) {
		element?.remove();
		return;
	}
	if (!element) {
		element = document.createElement('meta');
		element.setAttribute('name', name);
		document.head.appendChild(element);
	}
	element.setAttribute('content', content);
};

const setCanonical = (href: string | null): void => {
	let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
	if (!href) {
		element?.remove();
		return;
	}
	if (!element) {
		element = document.createElement('link');
		element.setAttribute('rel', 'canonical');
		document.head.appendChild(element);
	}
	element.setAttribute('href', href);
};

/**
 * Writes the head data into the document: title, description, keywords, canonical, the sharing tags and the structured data.
 */
export const applyDocumentMeta = (metas: DocumentMetaInterface): void => {
	if (metas.title) {
		document.title = metas.title;
	}
	setNamedMeta('description', metas.description);
	setNamedMeta('keywords', metas.keywords);
	setCanonical(metas.canonical);

	document.head.querySelectorAll(SHARING_TAGS).forEach((element) => element.remove());
	metas.seo_manager.forEach((tag) => {
		const element = document.createElement('meta');
		element.setAttribute(tag.attribute, tag.name);
		element.setAttribute('content', tag.content);
		document.head.appendChild(element);
	});

	document.head.querySelectorAll('script[type="application/ld+json"]').forEach((element) => element.remove());
	metas.json_ld.forEach((item) => {
		const script = document.createElement('script');
		script.type = 'application/ld+json';
		// A "<" can never close the script
		script.textContent = JSON.stringify(item).replace(/</g, '\\u003c');
		document.head.appendChild(script);
	});
};

let lastRequest = 0;

/**
 * Refreshes the head at a route change of the application. The first page comes with the right head in the HTML answer.
 */
export const refreshDocumentMeta = (to: RouteLocationNormalized): void => {
	const request = ++lastRequest;
	const page = Number(to.query.page);

	axios.get('/api/meta', {params: {path: to.path, page: Number.isInteger(page) && page > 1 ? page : undefined}}).then((response: any) => {
		// A slower answer of an earlier page must not overwrite the head of the current one
		if (request === lastRequest && response.data?.success === true) {
			applyDocumentMeta(response.data.metas);
		}
	}).catch(() => {
		// The head stays as it was: an error here must not disturb the page
	});
};
